import { Toggle } from '../../components/Toggle';
import { formatMetricName } from '../../utils/formatMetric';
import {
  AlarmTableRow,
  alarmRowAlarms,
  alarmRowMachines,
  alarmRowName,
} from './alarmRows';

interface AlarmListItemProps {
  row: AlarmTableRow;
  onToggleEnabled: (row: AlarmTableRow) => Promise<void>;
  onClick?: (row: AlarmTableRow) => void;
  /** False for another user's alert, which can be looked at but not switched. */
  canEdit?: boolean;
}

/**
 * One alert of the mobile list. The list has no expanding, so a grouped alert
 * is one item that sums up its signals, with the one toggle the alert has.
 */
export function AlarmListItemMobile({
  row,
  onToggleEnabled,
  canEdit = true,
  onClick,
}: AlarmListItemProps) {
  const alarms = alarmRowAlarms(row);
  const machineCount = alarmRowMachines(row).length;
  const signalSummary =
    alarms.length > 1
      ? `${alarms.length} signals`
      : formatMetricName(alarms[0].signal);

  return (
    <div
      className={`border-border-primary flex items-start gap-4 border-b px-4
        py-4 ${row.__highlighted ? 'highlight-flash' : ''}`}
      role='button'
      tabIndex={0}
      onClick={() => onClick?.(row)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.(row);
        }
      }}
    >
      {/* Content */}
      <div className='flex min-w-0 flex-1 items-start justify-between'>
        {/* Title and metadata */}
        <div className='flex min-w-0 flex-col gap-1'>
          <h3 className='size-p1 text-link truncate font-medium'>
            {alarmRowName(row)}
          </h3>
          <div className='size-caption text-secondary flex items-center gap-1'>
            <span className='truncate'>{signalSummary}</span>
            <span>·</span>
            <span className='whitespace-nowrap'>
              {machineCount} {machineCount === 1 ? 'machine' : 'machines'}
            </span>
          </div>
        </div>

        {/* Toggle */}
        {canEdit && (
          <div onClick={(e) => e.stopPropagation()}>
            <Toggle
              className='ml-2'
              checked={alarms.every((alarm) => alarm.isEnabled)}
              onCheckedChange={() => onToggleEnabled(row)}
              aria-label='Toggle Pulse alert'
            />
          </div>
        )}
      </div>
    </div>
  );
}
